import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const ICONS = {
  "user-addresses": [ContactMailOutlinedIcon, LocationOnOutlinedIcon, "#2563eb", "#dbeafe"],
  "payment-gateway": [CreditCardOutlinedIcon, GppGoodOutlinedIcon, "#c3171d", "#fee2e2"],
  "product-decoration": [CheckroomOutlinedIcon, CheckCircleIcon, "#7c3aed", "#ede9fe"],
  "branded-gateway": [StorefrontOutlinedIcon, LockOutlinedIcon, "#15803d", "#dcfce7"],
  "email-domain-guard": [MarkEmailReadOutlinedIcon, GppGoodOutlinedIcon, "#d97706", "#fef3c7"],
};

export const PromoStoreAddonIcon = ({ definition, active = false }) => {
  const [Primary = SettingsIcon, Accent = CheckCircleIcon, color = "#5f6b7a", soft = "#eef2f7"] = ICONS[definition?.icon || definition?.key] || [];
  return (
    <Box aria-hidden="true" sx={{ position: "relative", width: 88, height: 88, flex: "0 0 88px", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: soft, color, border: active ? `2px solid ${color}` : `1px solid ${soft}` }}>
      <Primary sx={{ fontSize: 44 }} />
      <Box sx={{ position: "absolute", right: -5, bottom: -5, width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: active ? color : "#fff", color: active ? "#fff" : color, border: "2px solid #fff" }}>
        <Accent sx={{ fontSize: 16 }} />
      </Box>
    </Box>
  );
};

export const PROMO_STORE_ADDON_STATUS_LABELS = { available: "Available", included: "Included", "coming-soon": "Coming Soon", custom: "Custom Program", contact: "Contact Elite" };

const PromoStoreMarketplaceCard = ({ definition, onViewDetails }) => (
  <Box
    component="article"
    sx={{
      borderRadius: 3,
      border: "1px solid",
      borderColor: "#e4e8ef",
      bgcolor: "background.paper",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      height: "100%",
      overflow: "hidden",
      boxShadow: "0 4px 14px rgba(31, 41, 55, 0.05)",
      transition: "box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease",
      "&:hover": { borderColor: "#c9d1dc", boxShadow: "0 10px 24px rgba(31, 41, 55, 0.10)", transform: "translateY(-2px)" },
      "&:focus-within": { borderColor: "primary.main", boxShadow: "0 0 0 3px rgba(195, 23, 29, 0.14)" },
    }}
  >
    <Box sx={{ minHeight: 132, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(145deg, #f8fafc 0%, #eef2f7 100%)" }}>
      <Chip label={PROMO_STORE_ADDON_STATUS_LABELS[definition.status] || definition.status} size="small" color={definition.status === "available" ? "success" : "default"} sx={{ position: "absolute", top: 12, right: 12, fontWeight: 700 }} />
      <PromoStoreAddonIcon definition={definition} />
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, p: 2 }}>
      {definition.category?.name ? <Typography variant="overline" color="primary.main" fontWeight={800} sx={{ lineHeight: 1.4 }}>{definition.category.name}</Typography> : null}
      <Typography component="h3" variant="subtitle1" fontWeight={800} sx={{ mt: 0.4 }}>{definition.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}>{definition.description}</Typography>
      <Button onClick={onViewDetails} variant="text" sx={{ alignSelf: "flex-start", mt: "auto", pt: 2, px: 0, textTransform: "none", fontWeight: 800 }}>View Details</Button>
    </Box>
  </Box>
);

const PromoStoreAddonConfigurationCard = ({ definition, active, onToggle, onConfigure }) => {
  const activatable = definition?.activatable !== false && definition?.status !== "coming-soon";
  const managedInOverview = definition.key === "payment-gateway";
  return (
  <Box sx={{ position: "relative", borderRadius: 3, border: active ? "2px solid #c3171d" : "1px solid", borderColor: active ? "#c3171d" : "divider", p: 2.25, bgcolor: active ? "rgba(195,23,29,0.03)" : "background.paper", display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0, height: "100%", boxShadow: "0 5px 18px rgba(15,23,42,0.06)" }}>
    <Chip label={!activatable ? "Coming Soon" : active ? "Active" : "Disabled"} size="small" sx={{ position: "absolute", top: 14, right: 14, fontWeight: 800 }} />
    {activatable && active && <IconButton aria-label={`Configure ${definition.name}`} size="small" onClick={onConfigure} sx={{ position: "absolute", top: 10, right: 96 }}><SettingsIcon fontSize="small" /></IconButton>}
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, pr: 8, flexDirection: { xs: "column", sm: "row" } }}>
      <PromoStoreAddonIcon definition={definition} active={active} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" fontWeight={800}>{definition.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>{definition.description}</Typography>
      </Box>
    </Box>
    <Divider sx={{ mt: "auto" }} />
    {activatable ? (
      <>
        <FormControlLabel control={<Switch checked={active} disabled={managedInOverview} onChange={(event) => onToggle?.(event.target.checked)} />} label={managedInOverview ? "Managed in Overview" : active ? "Enabled" : "Enable this add-on"} />
        <Typography variant="caption" color="text.secondary">{managedInOverview ? "Request Quote and Credit Card in Overview determine whether this add-on is active." : "This will be pushed to WordPress on “Initiate Sync”."}</Typography>
      </>
    ) : (
      <Typography variant="caption" color="text.secondary">Coming Soon — this capability is not configurable for individual PromoStores yet.</Typography>
    )}
  </Box>
  );
};

const PromoStoreAddonCard = ({ definition, context = "marketplace", active = false, onToggle, onConfigure, onViewDetails }) => {
  if (context === "configuration") return <PromoStoreAddonConfigurationCard definition={definition} active={active} onToggle={onToggle} onConfigure={onConfigure} />;
  return <PromoStoreMarketplaceCard definition={definition} onViewDetails={onViewDetails} />;
};

export default PromoStoreAddonCard;
